import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {environment} from '../../../../environments/environment';
// @ts-ignore
import * as mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
// @ts-ignore
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import {
  addLayersToMap,
  adjustMapBBox,
  DiscoveryEditEngine,
  GenericZoneEditEngine,
  GeoCoords,
  HazardZoneEditEngine,
  isStartZoneCircle,
  MapboxSpecificService,
  MapControlPanelAction,
  MapControlPanelRequestBody,
  MapEditEngine,
  MapEditEngineInstancesCluster,
  MarkerFindEditEngine,
  MarkerReachEditEngine,
  metersToPretty,
  numberToAtLeastTwoCharsStr,
  ObeliskComment,
  Pathway,
  QGraph,
  QGraphVertex,
  QGraphVertexWithCircle,
  QGraphVertexWithRadius, QuestMode,
  QuestStepObjectiveType,
  StartZoneCircleEditEngine,
  timerFromString,
  TimeSpan
} from 'quest-atlas-angular-components';
import {BehaviorSubject, Subject, take, takeUntil} from 'rxjs';
import {
  isTypeMapEditorQuestObjectiveDiscovery,
  isTypeMapEditorQuestObjectiveMarkerFind,
  isTypeMapEditorQuestObjectiveMarkerReach,
  MapEditorExternalApi,
  MapEditorQuestObjective,
  MapEditorQuestObjectiveDiscovery,
  MapEditorQuestObjectiveMarkerFind,
  MapEditorQuestObjectiveMarkerReach
} from './map-editor.interfaces';
import {FormBuilder, FormGroup} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@ngneat/transloco';

export interface MapConfigData {
  questMode?: QuestMode,
  mapObjectives?: MapEditorQuestObjective[];
  startZone?: QGraph | QGraphVertexWithRadius;
  hazardZones?: QGraph[];
  startZoneHidden?: boolean;
  timer?: TimeSpan;
  overallMap?: boolean;
  pathways?: Pathway[];
  comments?: ObeliskComment[];
  allMarkersReach?: {step: number, vertex: QGraphVertex}[];
  allMarkersFind?: {step: number, vertex: QGraphVertexWithCircle}[];
  allDiscoveries?: {step: number, graph: QGraph}[];
}

@Component({
    selector: 'app-map-editor',
    templateUrl: './map-editor.component.html',
    styleUrls: ['./map-editor.component.scss'],
    standalone: false
})
export class MapEditorComponent implements OnInit, MapEditorExternalApi, OnDestroy {
  readonly QuestStepObjectiveType = QuestStepObjectiveType;

  editMode: 'reachMarker' | 'findMarker' | 'discover' | 'startZone' | 'hazardZone' | 'activeZone' | 'markerPath' | null = null;
  currentEditEngine?: MapEditEngine;

  map!: mapboxgl.Map;
  mapLoaded = false;

  @Input() title = '';
  @Input() config?: MapConfigData = {};
  @Input() userGeo?: GeoCoords;

  @Output() close = new EventEmitter<void>();
  @Output() applyClick = new EventEmitter<MapConfigData>();

  markerReachEditEngines = new Map<number, MarkerReachEditEngine>();
  markerFindEditEngines = new Map<number, MarkerFindEditEngine>();
  discoveryEditEngines = new Map<number, DiscoveryEditEngine>();
  numberOfObjectives = 0;
  startZoneEditEngine: StartZoneCircleEditEngine;

  mapControlPanelActions$ = new Subject<MapControlPanelAction>();
  mapControlVisibility$ = new BehaviorSubject<MapControlPanelRequestBody>({ state: 'hide' });

  hazardZonesCluster: MapEditEngineInstancesCluster<HazardZoneEditEngine> = { instances: [] };

  isTimerVisible = false;

  form!: FormGroup;

  @HostListener('document:keyup', ['$event'])
  keyup(event: any) {
    event.stopPropagation();
  }

  destroy$ = new Subject<void>();

  constructor(private mapboxSpecificService: MapboxSpecificService, private fb: FormBuilder, private snackBar: MatSnackBar, private transloco: TranslocoService) {}
  ngOnInit(): void {
    // @ts-ignore
    mapboxgl.config.ACCESS_TOKEN = environment.mapboxPublicToke;
    let centerAndZoom = {
      zoom: 2,
      center: [0, 0]
    };
    const allCoords = [];

    const lastLocation = this.getLastLocation();

    for (const obj of this.config?.mapObjectives || []) {
      if (isTypeMapEditorQuestObjectiveMarkerFind(obj)) {
        // it's a super edge case, but may happen
        if (!obj.marker.coords) {
          continue
        }

        allCoords.push(obj.marker.coords);
      } else if (isTypeMapEditorQuestObjectiveMarkerReach(obj)) {
        if (!obj.marker.coords) {
          continue
        }

        allCoords.push(obj.marker.coords);
      } else if (isTypeMapEditorQuestObjectiveDiscovery(obj)) {
        const coords = obj.zone.connections.map((value) => obj.zone.vertices[value.startVertexId].coords);
        allCoords.push(...coords);
      }
    }

    if (lastLocation) {
      centerAndZoom = {
        zoom: 14,
        center: [lastLocation.lng, lastLocation.lat]
      };
    } else if (this.userGeo) {
      centerAndZoom = {
        zoom: 13,
        center: [this.userGeo.lng, this.userGeo.lat]
      };
    }

    // @ts-ignore
    this.map = new mapboxgl.Map({
      container: 'map-container',
      style: 'mapbox://styles/mapbox/outdoors-v12',
      ...centerAndZoom
    });

    //this is to support older quests, where start zones were polygons
    //remove this later
    if (!isStartZoneCircle(this.config?.startZone)) {
      this.config.startZone = null;
    }

    this.startZoneEditEngine = new StartZoneCircleEditEngine(this.map, this.mapboxSpecificService, this.config?.startZone as QGraphVertexWithRadius);
    this.startZoneEditEngine
      .mapControlPanelRequest$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.mapControlVisibility$.next(value);
      });
    this.startZoneEditEngine
      .stopEdit$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentEditEngine = undefined;
        this.editMode = null;
      });

    this.config?.hazardZones?.forEach((value) => {
      this.addNewHazardZone(value);
    });

    // Add map controls
    this.map.addControl(
      new MapboxGeocoder({
        accessToken: environment.mapboxPublicToke,
        mapboxgl: mapboxgl
      })
    );

    this.map.addControl(new mapboxgl.NavigationControl());

    this.map.setLanguage([navigator.language, 'en']);
    this.map.on('load', () => {
      this.mapLoaded = true;

      addLayersToMap(this.map);

      if (allCoords?.length) {
        adjustMapBBox(allCoords, this.map, 0, 0, 128);
      }

      for (let i = 0; i < (this.config?.mapObjectives || []).length; i++) {
        const obj = this.config?.mapObjectives[i];

        this.initNewMapEditEngine(obj, i);
        this.numberOfObjectives++;
      }
    });

    this.map.on('click', (ev: any) => {
      const coords = {
        lng: parseFloat(ev.lngLat.lng.toFixed(6)),
        lat: parseFloat(ev.lngLat.lat.toFixed(6))
      }

      if (this.currentEditEngine) {
        this.storeLastLocation(coords);
        this.currentEditEngine.clickOnMap(coords);
      }

      this.hazardZonesCluster.instances.forEach((value) => {
        if (!value.editing) {
          value.nonEditClickOnMap(coords);
        }
      });
    });

    this.form = this.fb.group({
      timer: [
        //todo move into utils file & refactor
        this.config.timer
          ? Object.values(this.config.timer).reduce(
              (acc, item, index) =>
                `${acc}${
                  index < Object.values(this.config.timer).length && index > 0
                    ? `:${numberToAtLeastTwoCharsStr(item)}`
                    : `${numberToAtLeastTwoCharsStr(item)}`
                }`,
              ''
            )
          : ''
      ]
    });
  }

  toggleMagStyle() {
    if (this.map.getStyle().name === 'Mapbox Outdoors') {
      this.map.setStyle('mapbox://styles/mapbox/satellite-v9');
    } else {
      this.map.setStyle('mapbox://styles/mapbox/outdoors-v12');
    }

    this.map.once('styledata', () => {
      addLayersToMap(this.map);

      setTimeout(() => {
        this.map.triggerRepaint();

        this.markerFindEditEngines.forEach((e) => e.externalRedraw());
        this.markerReachEditEngines.forEach((e) => e.externalRedraw());
        this.discoveryEditEngines.forEach((e) => e.externalRedraw());
        this.startZoneEditEngine.externalRedraw();

        this.hazardZonesCluster.instances.forEach((hz) => {
          hz.externalRedraw();
        });
      }, 100);
    });
  }

  apply(): MapConfigData {
    let timer: TimeSpan = timerFromString(this.form.get('timer')?.value as string);

    const result: MapConfigData = {
      startZone: this.startZoneEditEngine.getData().circleZone,
      hazardZones: this.hazardZonesCluster.instances
        .filter((value) => {
          return value.getData().cleanZone.connections.length >= 2;
        })
        .map((value) => {
          return value.getData().cleanZone;
        }),
      timer
    };

    // if (result.startZone && result.startZone.connections!.length < 2) {
    //   delete result.startZone
    // }

    result.mapObjectives = [];
    for (let i = 0; i < this.numberOfObjectives; i++) {
      if (this.markerFindEditEngines.get(i)) {
        // a super edge case, but I've seen it happen
        if (!this.markerFindEditEngines.get(i).getData().marker.coords?.lng) {
          continue;
        }

        result.mapObjectives.push({
          type: QuestStepObjectiveType.markerFind,
          marker: this.markerFindEditEngines.get(i).getData().marker
        } as MapEditorQuestObjectiveMarkerFind);
        continue;
      }
      if (this.markerReachEditEngines.get(i)) {
        // a super edge case, but I've seen it happen
        if (!this.markerReachEditEngines.get(i).getData().coords?.lng) {
          continue;
        }

        result.mapObjectives.push({
          type: QuestStepObjectiveType.markerReach,
          marker: {
            coords: this.markerReachEditEngines.get(i).getData().coords
          }
        } as MapEditorQuestObjectiveMarkerReach);
        continue;
      }
      if (this.discoveryEditEngines.get(i)) {
        result.mapObjectives.push({
          type: QuestStepObjectiveType.discovery,
          zone: this.discoveryEditEngines.get(i).getData().cleanZone,
          discoveryPercent: this.discoveryEditEngines.get(i).getData().discoveryPercent
        } as MapEditorQuestObjectiveDiscovery);
      }
    }

    return result;
  }

  isValid(): boolean {
    for (const inst of this.hazardZonesCluster.instances) {
      if (!inst.isValid()) {
        return false;
      }
    }

    return this.startZoneEditEngine?.isValid()
      && (this.markerFindEditEngines.size === 0 || Array.from(this.markerFindEditEngines.values()).some(e => e.isValid()))
      && (this.markerReachEditEngines.size === 0 || Array.from(this.markerReachEditEngines.values()).some(e => e.isValid()))
      && (this.discoveryEditEngines.size === 0 || Array.from(this.discoveryEditEngines.values()).some(e => e.isValid()));
  }

  addNewHazardZone(zone?: QGraph): HazardZoneEditEngine {
    const onClickCallback = (zoneEngine: GenericZoneEditEngine) => {
      if (this.isEditingAnything()) {
        return;
      }

      this.clickHazardZoneBtn(zoneEngine);
    };

    const newHazarZone = new HazardZoneEditEngine(this.map, this.mapControlPanelActions$.asObservable(), this.hazardZonesCluster, zone, onClickCallback);

    this.hazardZonesCluster.instances.push(newHazarZone);

    //A little memory leak, because the subscription will linger after the zone was removed
    newHazarZone
      .mapControlPanelRequest$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.mapControlVisibility$.next(value);
      });

    newHazarZone
      .stopEdit$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentEditEngine = undefined;
        this.editMode = null;
      });

    newHazarZone
      .removeSelf$()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        const index = this.hazardZonesCluster.instances.indexOf(newHazarZone);
        this.removeHazardZone(index);
      });

    return newHazarZone;
  }

  removeHazardZone(index: number): void {
    this.hazardZonesCluster.instances.splice(index, 1)[0].clear();
    this.stopCurrentEdit();
  }

  clickStartZoneBtn(): void {
    const editModeWas = this.editMode;
    this.stopCurrentEdit();
    //if clicked on the same button, we just deactivate current edit mode
    if (editModeWas === 'startZone') {
      return;
    }

    this.currentEditEngine = this.startZoneEditEngine;
    this.currentEditEngine.startEditing();
    this.editMode = 'startZone';
  }

  clickHazardZoneBtn(zoneEngine: HazardZoneEditEngine): void {
    const sameZone = this.currentEditEngine === zoneEngine;
    this.stopCurrentEdit();

    if (sameZone) {
      return;
    }

    this.currentEditEngine = zoneEngine;
    this.currentEditEngine.startEditing();
    this.editMode = 'hazardZone';
  }

  clickStepDurationButton(): void {
    this.isTimerVisible = !this.isTimerVisible;
  }

  clickMarkerReachBtn(engine: MarkerReachEditEngine): void {
    const editModeWas = this.editMode;
    this.stopCurrentEdit();
    //if clicked on the same button, we just deactivate current edit mode
    if (editModeWas === 'reachMarker') {
      return;
    }

    this.currentEditEngine = engine;
    this.currentEditEngine.startEditing();
    this.editMode = 'reachMarker';
  }

  clickMarkerFindBtn(engine: MarkerFindEditEngine): void {
    const editModeWas = this.editMode;
    this.stopCurrentEdit();
    //if clicked on the same button, we just deactivate current edit mode
    if (editModeWas === 'findMarker') {
      return;
    }

    this.currentEditEngine = engine;
    this.currentEditEngine.startEditing();
    this.editMode = 'findMarker';
  }

  clickDiscoverBtn(engine: DiscoveryEditEngine): void {
    const editModeWas = this.editMode;
    this.stopCurrentEdit();
    //if clicked on the same button, we just deactivate current edit mode
    if (editModeWas === 'discover') {
      return;
    }

    this.currentEditEngine = engine;
    this.currentEditEngine.startEditing();
    this.editMode = 'discover';
  }

  stopCurrentEdit(): void {
    if (this.currentEditEngine) {
      this.currentEditEngine.stopEditing();
      this.currentEditEngine = undefined;
    }
    this.clearEmptyObjectives();

    this.editMode = null;
  }

  clearEmptyObjectives(): void {
    this.markerReachEditEngines.forEach((e) => {
      if (!e.isValid()) {
        e.clear();
      }
    });

    this.markerFindEditEngines.forEach((e) => {
      if (!e.isValid()) {
        e.clear();
      }
    });

    this.discoveryEditEngines.forEach((e) => {
      if (!e.isValid()) {
        e.clear();
      }
    });

  }

  isEditingAnything(): boolean {
    if (this.hazardZonesCluster?.instances) {
      for (const hz of this.hazardZonesCluster.instances) {
        if (hz.editing) {
          return true;
        }
      }
    }

    return this.startZoneEditEngine?.editing
      || Array.from(this.markerFindEditEngines.values()).some(e => e.editing)
      || Array.from(this.markerReachEditEngines.values()).some(e => e.editing)
      || Array.from(this.discoveryEditEngines.values()).some(e => e.editing);
  }

  goToMyLocation(): void {
    if (this.userGeo) {
      this.map.setCenter([this.userGeo.lng, this.userGeo.lat]);
    } else {
      navigator.geolocation.getCurrentPosition((value) => {
        this.userGeo = { lng: value.coords.longitude, lat: value.coords.latitude };
        this.map.setCenter([this.userGeo.lng, this.userGeo.lat]);
      });
    }
  }

  addObjective(type: QuestStepObjectiveType): void {
    this.stopCurrentEdit();

    let newObj: MapEditorQuestObjective = {type};

    this.config?.mapObjectives?.push(newObj);
    this.currentEditEngine = this.initNewMapEditEngine(newObj, this.numberOfObjectives);
    this.currentEditEngine.startEditing();
    this.editMode = type === QuestStepObjectiveType.markerFind ? 'findMarker' : type === QuestStepObjectiveType.markerReach ? 'reachMarker' : 'discover';
    this.numberOfObjectives++;
  }

  private storeLastLocation(coords: GeoCoords): void {
    localStorage.setItem('lastLocation', JSON.stringify(coords));
  }

  removeObjective(index: number): void {
    this.stopCurrentEdit();

    if (this.config?.mapObjectives) {
      this.markerReachEditEngines.get(index)?.destroy();
      this.markerFindEditEngines.get(index)?.destroy();
      this.discoveryEditEngines.get(index)?.destroy();
      this.markerReachEditEngines.delete(index);
      this.markerFindEditEngines.delete(index);
      this.discoveryEditEngines.delete(index);
    }
  }

  importOverlayFromGPX(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.gpx,application/gpx+xml';
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event: any) => {
        try {
          const text = event.target.result;
          const coords = [];

          const gpx = new DOMParser().parseFromString(text, 'application/xml');
          const trkpts = gpx.getElementsByTagName('trkpt');
          for (let i = 0; i < trkpts.length; i++) {
            const lat = parseFloat(trkpts[i].getAttribute('lat')!);
            const lng = parseFloat(trkpts[i].getAttribute('lon')!);
            coords.push({lat, lng});
          }

          if (coords.length < 2) {
            throw new Error('Not enough coordinates in GPX file');
          }

          console.log('Imported GPX coords', coords);

          adjustMapBBox(coords, this.map, 0, 0, 128);

          // add a GEOJson Feature of lineString to the map as overlay
          const line = turf.lineString(coords.map(c => [c.lng, c.lat]));

          this.map.addSource('gpx-overlay', {
            type: 'geojson',
            data: line
          });

          this.map.addLayer({
            id: 'gpx-overlay-line',
            type: 'line',
            source: 'gpx-overlay',
            layout: {},
            paint: {
              'line-color': '#ff0000',
              'line-width': 3,
              // 'line-opacity': 0.8,
            }
          });
        } catch (error) {
          console.log('Error parsing GPX file', error);
          this.snackBar.open(
            this.transloco.translate('invalidGpxFile'),
            this.transloco.translate('close'),
            {
              verticalPosition: 'bottom',
              duration: 5000,
              panelClass: 'error-snackbar'
            });
        }
      }
      reader.readAsText(file);
    };
    input.click();
  }

  removeOverlayGPX(): void {
    if (this.map.getLayer('gpx-overlay-line')) {
      this.map.removeLayer('gpx-overlay-line');
    }
    if (this.map.getSource('gpx-overlay')) {
      this.map.removeSource('gpx-overlay');
    }
  }

  isOverlayAdded(): boolean {
    return !!this.map.getLayer('gpx-overlay-line');
  }

  private getLastLocation(): GeoCoords | null {
    const ll = localStorage.getItem('lastLocation');
    return ll ? JSON.parse(ll) : null;
  }

  private initNewMapEditEngine(obj: MapEditorQuestObjective, i: number) {
    let engine: MarkerReachEditEngine | MarkerFindEditEngine | DiscoveryEditEngine;
    if (isTypeMapEditorQuestObjectiveMarkerFind(obj)) {
      engine = new MarkerFindEditEngine(this.map, this.mapboxSpecificService, obj.marker);
      this.markerFindEditEngines.set(i, engine);
    } else if (isTypeMapEditorQuestObjectiveMarkerReach(obj)) {
      engine =  new MarkerReachEditEngine(this.map, this.mapboxSpecificService, obj.marker?.coords);
      this.markerReachEditEngines.set(i, engine);
    } else if (isTypeMapEditorQuestObjectiveDiscovery(obj)) {
      engine = new DiscoveryEditEngine(this.map, this.mapControlPanelActions$.asObservable(), obj.zone);
      engine.setDiscoveryPercent(obj.discoveryPercent)
      this.discoveryEditEngines.set(i, engine);
    }

    //this is, so that from inside edit engines we can show map controls buttons
    engine.mapControlPanelRequest$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.mapControlVisibility$.next(value);
      });
    engine.stopEdit$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentEditEngine = undefined;
        this.editMode = null;
      });

    return engine;
  }

  ngOnDestroy(): void {
    this.mapControlPanelActions$.complete();
    this.mapControlVisibility$.complete();
    this.destroy$.next();
    this.destroy$.complete();

    this.startZoneEditEngine?.destroy();
    this.markerReachEditEngines.forEach(e => e.destroy());
    this.markerFindEditEngines.forEach(e => e.destroy());
    this.discoveryEditEngines.forEach(e => e.destroy());
    this.hazardZonesCluster.instances.forEach((value) => {
      value.destroy();
    });
  }

  metersToPretty = metersToPretty;
  protected readonly QuestMode = QuestMode;
}
